import React, { useState, useEffect } from 'react';
import { api } from '../config/api';
import { FiPhone, FiMail, FiUser, FiSearch, FiPlus, FiEdit2, FiTrash2, FiStar, FiMessageSquare, FiExternalLink, FiSend, FiX } from 'react-icons/fi';
import toast from 'react-hot-toast';
import { formatDistanceToNow } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { tw, monoFont } from '../utils/terminalTheme';

interface Vendor {
  id: number;
  business_name: string;
  contact_name?: string;
  phone?: string;
  email?: string;
  service_type?: string;
  service_areas?: string[];
  accepts_email: boolean;
  accepts_sms: boolean;
  preferred_contact_method: string;
  is_active: boolean;
  is_preferred: boolean;
  notes?: string;
  created_at: string;
  contact_id?: number;
  has_recent_messages: boolean;
  last_message_date?: string;
}

const Vendors: React.FC = () => {
  const navigate = useNavigate();
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedServiceType, setSelectedServiceType] = useState('');
  const [serviceTypes, setServiceTypes] = useState<string[]>([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState<Vendor | null>(null);
  const [showVendorDetail, setShowVendorDetail] = useState(false);
  const [messageText, setMessageText] = useState('');

  const [formData, setFormData] = useState({
    business_name: '',
    contact_name: '',
    phone: '',
    email: '',
    service_type: '',
    service_areas: [] as string[],
    accepts_email: true,
    accepts_sms: true,
    preferred_contact_method: 'sms',
    is_preferred: false,
    notes: ''
  });

  useEffect(() => {
    fetchServiceTypes();
    fetchVendors();
  }, [selectedServiceType]);

  const fetchServiceTypes = async () => {
    try {
      const response = await api.get('/vendors/service-types');
      setServiceTypes(response.data);
    } catch (error) {
      console.error('Error fetching service types:', error);
    }
  };

  const fetchVendors = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append('is_active', 'true');
      if (selectedServiceType) params.append('service_type', selectedServiceType);
      const response = await api.get(`/vendors/?${params}`);
      setVendors(response.data);
    } catch (error) {
      toast.error('Failed to load vendors');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (showEditModal && selectedVendor) {
        await api.put(`/vendors/${selectedVendor.id}`, formData);
        toast.success('Vendor updated successfully');
      } else {
        await api.post('/vendors/', formData);
        toast.success('Vendor created successfully');
      }
      resetForm();
      fetchVendors();
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to save vendor');
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to deactivate this vendor?')) {
      try {
        await api.delete(`/vendors/${id}`);
        toast.success('Vendor deactivated');
        fetchVendors();
      } catch (error) {
        toast.error('Failed to delete vendor');
      }
    }
  };

  const handleEdit = (vendor: Vendor) => {
    setSelectedVendor(vendor);
    setFormData({
      business_name: vendor.business_name,
      contact_name: vendor.contact_name || '',
      phone: vendor.phone || '',
      email: vendor.email || '',
      service_type: vendor.service_type || '',
      service_areas: vendor.service_areas || [],
      accepts_email: vendor.accepts_email,
      accepts_sms: vendor.accepts_sms,
      preferred_contact_method: vendor.preferred_contact_method,
      is_preferred: vendor.is_preferred,
      notes: vendor.notes || ''
    });
    setShowEditModal(true);
  };

  const resetForm = () => {
    setFormData({
      business_name: '', contact_name: '', phone: '', email: '',
      service_type: '', service_areas: [], accepts_email: true,
      accepts_sms: true, preferred_contact_method: 'sms', is_preferred: false, notes: ''
    });
    setShowAddModal(false);
    setShowEditModal(false);
    setSelectedVendor(null);
  };

  const filteredVendors = vendors.filter(vendor => {
    const s = searchTerm.toLowerCase();
    return (
      vendor.business_name.toLowerCase().includes(s) ||
      vendor.contact_name?.toLowerCase().includes(s) ||
      vendor.phone?.includes(searchTerm) ||
      vendor.email?.toLowerCase().includes(s)
    );
  });

  const handleServiceAreaChange = (value: string) => {
    if (value && !formData.service_areas.includes(value)) {
      setFormData({ ...formData, service_areas: [...formData.service_areas, value] });
    }
  };

  const removeServiceArea = (area: string) => {
    setFormData({ ...formData, service_areas: formData.service_areas.filter(a => a !== area) });
  };

  return (
    <div className={tw.page}>
      {/* Terminal Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold font-mono text-white" style={monoFont}>
          <span className="text-cyan-400">~/</span>vendors
          <span className="text-gray-600 text-sm font-normal ml-3">
            <span className="text-green-500">$</span> contacts --list
          </span>
        </h1>

        <div className="flex flex-wrap gap-3 mt-4">
          <div className="flex-1 min-w-[300px] relative">
            <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-600" />
            <input
              type="text"
              placeholder="Search vendors..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={tw.input + ' pl-10'}
            />
          </div>

          <select
            value={selectedServiceType}
            onChange={(e) => setSelectedServiceType(e.target.value)}
            className={tw.select + ' w-auto'}
          >
            <option value="">All Services</option>
            {serviceTypes.map(type => (
              <option key={type} value={type}>{type}</option>
            ))}
          </select>

          <button onClick={() => setShowAddModal(true)} className={tw.btnPrimary + ' flex items-center gap-2'}>
            <FiPlus className="w-4 h-4" />
            Add Vendor
          </button>
        </div>
      </div>

      {loading ? (
        <div className={tw.loadingContainer}>
          <div className={tw.loading}></div>
        </div>
      ) : (
        <div className="grid gap-3">
          {filteredVendors.map(vendor => (
            <div
              key={vendor.id}
              className="bg-gray-800/80 border border-gray-700/50 rounded-lg p-4 hover:border-cyan-500/30 transition-all cursor-pointer group"
              onClick={() => { setSelectedVendor(vendor); setShowVendorDetail(true); }}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="text-sm font-mono font-semibold text-white">{vendor.business_name}</h3>
                    {vendor.is_preferred && (
                      <FiStar className="text-amber-400 fill-current w-3.5 h-3.5" title="Preferred Vendor" />
                    )}
                    {vendor.has_recent_messages && (
                      <div className="flex items-center gap-1 text-cyan-400 text-[10px] font-mono">
                        <FiMessageSquare className="w-3 h-3" />
                        <span title={`Last message: ${vendor.last_message_date ? formatDistanceToNow(new Date(vendor.last_message_date), { addSuffix: true }) : 'Unknown'}`}>
                          Recent activity
                        </span>
                      </div>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 text-xs font-mono">
                    {vendor.contact_name && (
                      <div className="flex items-center gap-2 text-gray-400">
                        <FiUser className="text-gray-600 w-3 h-3" />
                        {vendor.contact_name}
                      </div>
                    )}
                    {vendor.phone && (
                      <div className="flex items-center gap-2 text-gray-400">
                        <FiPhone className="text-gray-600 w-3 h-3" />
                        {vendor.phone}
                        {vendor.accepts_sms && <span className="text-green-500 text-[10px]">(SMS)</span>}
                      </div>
                    )}
                    {vendor.email && (
                      <div className="flex items-center gap-2 text-gray-400">
                        <FiMail className="text-gray-600 w-3 h-3" />
                        {vendor.email}
                      </div>
                    )}
                    {vendor.service_type && (
                      <div>
                        <span className="inline-flex px-2 py-0.5 rounded text-[10px] font-mono bg-cyan-500/15 text-cyan-400 border border-cyan-500/30">
                          {vendor.service_type}
                        </span>
                      </div>
                    )}
                  </div>

                  {vendor.notes && (
                    <p className="mt-2 text-[11px] text-gray-500 font-mono">{vendor.notes}</p>
                  )}
                </div>

                <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity ml-3">
                  <button
                    onClick={(e) => { e.stopPropagation(); handleEdit(vendor); }}
                    className="p-1.5 text-gray-500 hover:text-cyan-400 transition-colors"
                  >
                    <FiEdit2 className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); handleDelete(vendor.id); }}
                    className="p-1.5 text-gray-500 hover:text-red-400 transition-colors"
                  >
                    <FiTrash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            </div>
          ))}

          {filteredVendors.length === 0 && (
            <div className={tw.empty}>No vendors found</div>
          )}
        </div>
      )}

      {/* Add/Edit Modal */}
      {(showAddModal || showEditModal) && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50">
          <div className={tw.formWrap + ' w-full max-w-2xl max-h-[90vh] overflow-y-auto'}>
            <h2 className={tw.formTitle}>
              {showEditModal ? 'Edit Vendor' : 'Add New Vendor'}
            </h2>

            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className={tw.label}>Business Name *</label>
                  <input type="text" required value={formData.business_name}
                    onChange={(e) => setFormData({ ...formData, business_name: e.target.value })}
                    className={tw.input} />
                </div>
                <div>
                  <label className={tw.label}>Contact Name</label>
                  <input type="text" value={formData.contact_name}
                    onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                    className={tw.input} />
                </div>
                <div>
                  <label className={tw.label}>Phone</label>
                  <input type="tel" value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className={tw.input} />
                </div>
                <div>
                  <label className={tw.label}>Email</label>
                  <input type="email" value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className={tw.input} />
                </div>
                <div>
                  <label className={tw.label}>Service Type</label>
                  <select value={formData.service_type}
                    onChange={(e) => setFormData({ ...formData, service_type: e.target.value })}
                    className={tw.select}>
                    <option value="">Select service type</option>
                    {serviceTypes.map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={tw.label}>Preferred Contact</label>
                  <select value={formData.preferred_contact_method}
                    onChange={(e) => setFormData({ ...formData, preferred_contact_method: e.target.value })}
                    className={tw.select}>
                    <option value="sms">SMS</option>
                    <option value="email">Email</option>
                    <option value="phone">Phone</option>
                  </select>
                </div>
              </div>

              <div className="mt-4">
                <label className={tw.label}>Service Areas (Zip Codes)</label>
                <div className="flex gap-2 mb-2">
                  <input type="text" placeholder="Enter zip code and press Enter"
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        const input = e.target as HTMLInputElement;
                        handleServiceAreaChange(input.value);
                        input.value = '';
                      }
                    }}
                    className={tw.input} />
                </div>
                <div className="flex flex-wrap gap-2">
                  {formData.service_areas.map(area => (
                    <span key={area} className="px-2 py-0.5 bg-gray-700 text-gray-300 rounded text-xs font-mono flex items-center gap-1">
                      {area}
                      <button type="button" onClick={() => removeServiceArea(area)} className="text-red-400 hover:text-red-300">
                        <FiX className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              </div>

              <div className="mt-4 space-y-2">
                <label className="flex items-center gap-2 text-gray-400 text-xs font-mono">
                  <input type="checkbox" checked={formData.accepts_email}
                    onChange={(e) => setFormData({ ...formData, accepts_email: e.target.checked })}
                    className="rounded bg-gray-700 border-gray-600" />
                  Accepts Email
                </label>
                <label className="flex items-center gap-2 text-gray-400 text-xs font-mono">
                  <input type="checkbox" checked={formData.accepts_sms}
                    onChange={(e) => setFormData({ ...formData, accepts_sms: e.target.checked })}
                    className="rounded bg-gray-700 border-gray-600" />
                  Accepts SMS
                </label>
                <label className="flex items-center gap-2 text-gray-400 text-xs font-mono">
                  <input type="checkbox" checked={formData.is_preferred}
                    onChange={(e) => setFormData({ ...formData, is_preferred: e.target.checked })}
                    className="rounded bg-gray-700 border-gray-600" />
                  Preferred Vendor
                </label>
              </div>

              <div className="mt-4">
                <label className={tw.label}>Notes</label>
                <textarea value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows={3} className={tw.input} />
              </div>

              <div className="mt-6 flex justify-end gap-3">
                <button type="button" onClick={resetForm} className={tw.btnSecondary}>Cancel</button>
                <button type="submit" className={tw.btnPrimary}>
                  {showEditModal ? 'Update' : 'Create'} Vendor
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Vendor Detail Modal */}
      {showVendorDetail && selectedVendor && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50">
          <div className={tw.formWrap + ' w-full max-w-2xl max-h-[90vh] overflow-y-auto'}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-mono text-cyan-400 flex items-center gap-2">
                {selectedVendor.business_name}
                {selectedVendor.is_preferred && <FiStar className="text-amber-400 fill-current w-4 h-4" />}
              </h2>
              <button onClick={() => { setShowVendorDetail(false); setMessageText(''); }}
                className="text-gray-500 hover:text-white transition-colors">
                <FiX size={20} />
              </button>
            </div>

            <div className="space-y-5">
              <div>
                <h3 className="text-xs font-mono text-gray-500 uppercase tracking-wider mb-3">Contact Information</h3>
                <div className="space-y-2">
                  {selectedVendor.contact_name && (
                    <div className="flex items-center gap-2 text-gray-400 text-xs font-mono">
                      <FiUser className="text-gray-600 w-3.5 h-3.5" /> {selectedVendor.contact_name}
                    </div>
                  )}
                  {selectedVendor.phone && (
                    <div className="flex items-center gap-2 text-gray-400 text-xs font-mono">
                      <FiPhone className="text-gray-600 w-3.5 h-3.5" /> {selectedVendor.phone}
                      {selectedVendor.accepts_sms && <span className="text-green-500 text-[10px]">(SMS enabled)</span>}
                    </div>
                  )}
                  {selectedVendor.email && (
                    <div className="flex items-center gap-2 text-gray-400 text-xs font-mono">
                      <FiMail className="text-gray-600 w-3.5 h-3.5" /> {selectedVendor.email}
                      {selectedVendor.accepts_email && <span className="text-green-500 text-[10px]">(Email enabled)</span>}
                    </div>
                  )}
                </div>
              </div>

              <div>
                <h3 className="text-xs font-mono text-gray-500 uppercase tracking-wider mb-3">Service Details</h3>
                <div className="space-y-2 text-xs font-mono text-gray-400">
                  {selectedVendor.service_type && (
                    <div><span className="text-gray-600">Type:</span> {selectedVendor.service_type}</div>
                  )}
                  {selectedVendor.service_areas && selectedVendor.service_areas.length > 0 && (
                    <div>
                      <span className="text-gray-600">Areas:</span>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {selectedVendor.service_areas.map(area => (
                          <span key={area} className="px-2 py-0.5 bg-gray-700/50 rounded text-[10px] font-mono text-gray-300">{area}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  <div><span className="text-gray-600">Preferred Contact:</span> {selectedVendor.preferred_contact_method}</div>
                </div>
              </div>

              {selectedVendor.notes && (
                <div>
                  <h3 className="text-xs font-mono text-gray-500 uppercase tracking-wider mb-3">Notes</h3>
                  <p className="text-xs text-gray-400 font-mono">{selectedVendor.notes}</p>
                </div>
              )}

              <div className="border-t border-gray-700/50 pt-5">
                <h3 className="text-xs font-mono text-gray-500 uppercase tracking-wider mb-3">Communication</h3>
                <div className="space-y-3">
                  <button
                    onClick={() => {
                      if (selectedVendor.phone) navigate('/messages', { state: { selectedPhone: selectedVendor.phone } });
                    }}
                    className={tw.btnPrimary + ' w-full flex items-center justify-center gap-2'}
                  >
                    <FiMessageSquare /> View Conversation <FiExternalLink size={14} />
                  </button>

                  {selectedVendor.phone && selectedVendor.accepts_sms && (
                    <div>
                      <label className={tw.label}>Send Quick SMS</label>
                      <div className="flex gap-2">
                        <input type="text" value={messageText}
                          onChange={(e) => setMessageText(e.target.value)}
                          placeholder="Type a message..."
                          className={tw.input} />
                        <button
                          onClick={() => toast('SMS functionality will be implemented with OpenPhone integration')}
                          className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md font-mono text-sm transition-colors"
                          disabled={!messageText.trim()}>
                          <FiSend />
                        </button>
                      </div>
                    </div>
                  )}

                  {selectedVendor.email && selectedVendor.accepts_email && (
                    <a href={`mailto:${selectedVendor.email}`}
                      className={tw.btnSecondary + ' w-full flex items-center justify-center gap-2'}>
                      <FiMail /> Send Email <FiExternalLink size={14} />
                    </a>
                  )}

                  {selectedVendor.phone && (
                    <a href={`tel:${selectedVendor.phone}`}
                      className={tw.btnSecondary + ' w-full flex items-center justify-center gap-2'}>
                      <FiPhone /> Call {selectedVendor.phone} <FiExternalLink size={14} />
                    </a>
                  )}
                </div>
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <button onClick={() => { setShowVendorDetail(false); handleEdit(selectedVendor); }} className={tw.btnGhost}>
                Edit Vendor
              </button>
              <button onClick={() => { setShowVendorDetail(false); setMessageText(''); }} className={tw.btnSecondary}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Vendors;
